// Spaces are channels with type='space'. Mutator export name preserved
// (`clientSpacesMutators`) so callers don't change in this PR; cosmetic
// rename to `spacesMutators` happens in a follow-up.

import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";

export const clientSpacesMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      name: z.string(),
      description: z.string().optional(),
      created_by: z.string(),
      isPrivate: z.boolean().optional(),
      created_at: z.number(),
      // Deterministic row IDs for the two side-table inserts. Generated
      // once by the caller and threaded through args so the client-side
      // (optimistic) and server-side (authoritative) runs of this mutator
      // agree on the PK. Without this, each run calls `crypto.randomUUID()`
      // independently, the server INSERT fails the UNIQUE (channel_id,
      // user_id) / UNIQUE (channel_id, workspace_id) constraints, and the
      // transaction aborts (per `.claude/rules/mutators.md` — "Generate IDs
      // and timestamps client-side"). Reported on PR #1250 by claude[bot].
      manager_membership_id: z.string(),
      host_link_id: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(args.workspace_id),
          makeUserId(ctx.sub),
        );
      }
      const { isPrivate, manager_membership_id, host_link_id, ...channelArgs } =
        args;
      // WS-B13a follow-up: spaces inherit the workspace's region. Workspace
      // pinning means data + zero-cache for this workspace live in one
      // region; an EU-tagged workspace's spaces must also be 'eu' so they
      // remain reachable from the authoritative replica.
      const workspace = await tx.run(
        zql.workspaces.where("id", args.workspace_id).one(),
      );
      // Upsert the channels row representing the space. `posting_restriction`
      // gets the channels default 'everyone'; not user-visible for spaces.
      await tx.mutate.channels.upsert({
        ...channelArgs,
        type: "space",
        is_private: isPrivate ?? false,
        is_archived: false,
        posting_restriction: "everyone",
        region: workspace?.region ?? "us",
      });
      // Creator becomes a space manager (channel_members row with
      // is_channel_manager=true). Upsert so retries are safe; rejoin paths
      // elsewhere in the codebase use the same shape.
      await tx.mutate.channel_members.upsert({
        id: manager_membership_id,
        channel_id: args.id,
        user_id: args.created_by,
        is_channel_manager: true,
        joined_at: args.created_at,
        muted: false,
        notify: "all",
        hidden: false,
      });
      // Record the host workspace link so the space appears in the
      // host workspace's link list (matches the REST path in
      // `client-spaces-routes.ts:175-179`).
      await tx.mutate.channel_workspace_links.upsert({
        id: host_link_id,
        channel_id: args.id,
        workspace_id: args.workspace_id,
        linked_by: args.created_by,
        role: "host",
        created_at: args.created_at,
      });
    },
  ),

  update: defineMutator(
    z.object({
      id: z.string(),
      name: z.string().optional(),
      description: z.string().optional(),
      is_archived: z.boolean().optional(),
    }),
    async ({ tx, args, ctx }) => {
      let isCallerAdmin = false;
      if (tx.location === "server") {
        const space = await tx.run(
          zql.channels.where("id", args.id).where("type", "space").one(),
        );
        if (!space) return;
        // Workspace admin+ can manage
        if (space.workspace_id) {
          const wsRole = await tx.run(
            zql.workspace_members
              .where("workspace_id", space.workspace_id)
              .where("user_id", ctx.sub)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (wsRole && ADMIN_PLUS.includes(wsRole.role)) {
            isCallerAdmin = true;
          }
        }
        if (!isCallerAdmin) {
          // Space manager (is_channel_manager on the space row) can manage
          const spaceMembership = await tx.run(
            zql.channel_members
              .where("channel_id", args.id)
              .where("user_id", ctx.sub)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (!spaceMembership?.is_channel_manager) {
            throw new Error("Unauthorized: not admin or space manager");
          }
        }
      }
      await tx.mutate.channels.update(args);

      // Cascade archive/unarchive to all child channels.
      // Only admin+ cascades — channels.update requires admin or channel
      // manager auth. Space managers archive the space itself but its
      // child channels stay unarchived; sidebar `!ch.parent_id` filter
      // hides them from the standalone list regardless.
      // Filter children to `type='channel'` so a future nested-space
      // caller doesn't recursively archive a child SPACE (which would
      // hit `channelsMutators.update`'s `type === "space"` rejection
      // and abort the transaction). Today no caller creates nested
      // spaces, but the schema permits it via the self-FK.
      if (
        args.is_archived !== undefined &&
        tx.location === "server" &&
        isCallerAdmin
      ) {
        const children = await tx.run(
          zql.channels.where("parent_id", args.id).where("type", "channel"),
        );
        for (const ch of children) {
          await tx.mutate.channels.update({
            id: ch.id,
            is_archived: args.is_archived,
          });
        }
      }
    },
  ),

  delete: defineMutator(z.object({ id: z.string() }), async () => {
    // Server-authoritative — go through the server delete endpoint per
    // .claude/rules/mutators.md Pattern 7. Public client mutator hard-rejects
    // so optimistic client deletes don't run.
    throw new Error("Unauthorized: use server delete endpoint");
  }),
};
